/** Angle flows on Curate (facets 1c-2): edit / retire / restore / reorder with the
 * project-question review (D12). Owns dialogs + toasts; FacetBar is presentational.
 * Dialogs stay open until the save succeeds; facet ops serialize via useFacetBusy. */
import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { AddAngleSheet, type AddAngleMode } from "./AddAngleSheet";
import { BUSY_REASON, FacetBar } from "./FacetBar";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import { EditAngleDialog, type AngleFields } from "./EditAngleDialog";
import { ProjectQuestionReview } from "./ProjectQuestionReview";
import { toastFacetError } from "./toastFacetError";
import {
  useFacetBusy, useFacetList, useReorderFacets, useRestoreFacet, useRetireFacet, useUpdateFacet,
  type AngleText,
} from "@/hooks/useFacets";
import { REVIEW_CHANGE_FRACTION, angleCounts, changedFraction, seedCountsByFacet } from "@/lib/angles";
import { toast } from "@/lib/toast";
import type { Concept, ConceptScope, FacetDetail, FacetSummary, ProjectQuestion } from "@/types/concept";
import type { PaperWithRole } from "@/types/paper";

interface Props {
  concept: Concept;
  conceptId: string;
  /** Active angles, position order (concept.facet_list). */
  facets: FacetSummary[];
  abstract: string;
  keywords: string;
  conceptScope: ConceptScope | null | undefined;
  /** The table's scoped base (working set / camp / paperIdScope), dismissed excluded. */
  papers: PaperWithRole[];
  /** Seeds (foundational) for the 0-seed warning dot. */
  seeds: PaperWithRole[];
  selected: string | undefined;
  onSelect: (angle: string | undefined) => void;
  /** Angle pass retries exhausted (concept.angle_rescore_failed). */
  failed?: boolean;
  /** plan_limits.max_facets (null/undefined = no cap). The server 403 is still handled. */
  maxFacets?: number | null;
  /** Trawl from these seeds ("Trawl this angle"). */
  onTrawl?: (seeds: PaperWithRole[]) => void;
  /** A trawl is running: "Trawl this angle" is disabled. */
  trawling?: boolean;
}

/** `angles` is the proposed angle set, frozen when the review opens so cache writes
 * (optimistic restore append, concept polls) can't re-draft or duplicate it. */
type Review =
  | { kind: "retire"; facet: FacetSummary; angles: AngleText[] }
  | { kind: "restore"; facet: FacetDetail; angles: AngleText[] }
  | { kind: "edit"; facet: FacetSummary; fields: AngleFields; angles: AngleText[] };

type Editing = { facet: FacetSummary; initial?: AngleFields };

const RESCORE_COPY = "Angle scores will refresh in the background — this can take a few minutes.";
const text = (f: Pick<FacetSummary, "label" | "question">): AngleText => ({ label: f.label, question: f.question });

/** Why angle actions are dim while the Add-angle / Find-seeds sheet is open. */
const SHEET_OPEN_REASON = "Finish or close the angle sheet first.";

export function AngleManager({
  concept, conceptId, facets, abstract, keywords, conceptScope, papers, seeds, selected, onSelect, failed,
  maxFacets, onTrawl, trawling,
}: Props) {
  const navigate = useNavigate();
  const { data: all } = useFacetList(conceptId, true);
  const retired = useMemo(() => (all ?? []).filter((f) => f.retired_at), [all]);
  const counts = useMemo(() => angleCounts(papers, facets, conceptScope), [papers, facets, conceptScope]);
  const seedCounts = useMemo(() => seedCountsByFacet(seeds, facets), [seeds, facets]);
  const busy = useFacetBusy(conceptId);

  const retireM = useRetireFacet(conceptId);
  const restoreM = useRestoreFacet(conceptId);
  const reorderM = useReorderFacets(conceptId);
  const updateM = useUpdateFacet(conceptId);

  const [editing, setEditing] = useState<Editing | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [sheet, setSheet] = useState<AddAngleMode | null>(null);
  // Re-entry guard: a double click must not fire a second mutation before the first settles.
  const submittingRef = useRef(false);
  const begin = () => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    return true;
  };
  const settled = () => { submittingRef.current = false; };

  const fail = (fallback: string) => (err: unknown) =>
    void toastFacetError(err, fallback, () => navigate({ to: "/settings/billing" }));
  const done = (msg: string, rescores: boolean) => toast.success(rescores ? `${msg} ${RESCORE_COPY}` : msg);

  // One dialog at any angle count; down to one angle there is nothing to redraft.
  const onRetire = (f: FacetSummary) =>
    setReview({ kind: "retire", facet: f, angles: facets.filter((x) => x.id !== f.id).map(text) });
  const onRestore = (f: FacetDetail) =>
    setReview({ kind: "restore", facet: f, angles: [...facets.filter((x) => x.id !== f.id), f].map(text) });

  const onMove = (id: string, delta: -1 | 1) => {
    const ids = facets.map((f) => f.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorderM.mutate(ids, { onError: fail("Couldn't reorder angles.") });
  };

  const onSaveEdit = (fields: AngleFields) => {
    const f = editing!.facet;
    const big = fields.label !== undefined
      || (fields.question !== undefined && changedFraction(f.question, fields.question) > REVIEW_CHANGE_FRACTION);
    if (big) {
      setEditing(null);
      setReview({
        kind: "edit", facet: f, fields,
        angles: facets.map((x) => (x.id === f.id ? text({ ...x, ...fields }) : text(x))),
      });
      return;
    }
    if (!begin()) return;
    updateM.mutate({ facetId: f.id, ...fields }, {
      onSuccess: () => { setEditing(null); done("Angle saved.", fields.question !== undefined || fields.search_terms !== undefined); },
      onError: fail("Couldn't save the angle."),
      onSettled: settled,
    });
  };

  const submitReview = (pq: ProjectQuestion | undefined) => {
    const r = review!;
    if (!begin()) return;
    const extra = pq ? { projectQuestion: pq } : {};
    const pqRescores = !!pq && pq.choice !== "keep";
    const close = (msg: string, rescores: boolean) => () => { setReview(null); done(msg, rescores); };
    if (r.kind === "retire") {
      retireM.mutate({ facetId: r.facet.id, ...extra }, {
        onSuccess: () => {
          // Down to one angle there are no tabs (incl. Bridges): drop any selection.
          if (selected === r.facet.id || (selected !== undefined && r.angles.length < 2)) onSelect(undefined);
          close(`Retired ${r.facet.label}.`, pqRescores)();
        },
        onError: fail("Couldn't retire the angle."),
        onSettled: settled,
      });
    } else if (r.kind === "restore") {
      restoreM.mutate({ facetId: r.facet.id, facet: r.facet, ...extra }, {
        onSuccess: close(`Restored ${r.facet.label}.`, true),
        onError: fail("Couldn't restore the angle."),
        onSettled: settled,
      });
    } else {
      const angleRescores = r.fields.question !== undefined || r.fields.search_terms !== undefined;
      updateM.mutate({ facetId: r.facet.id, ...r.fields, ...extra }, {
        onSuccess: close("Angle saved.", angleRescores || pqRescores),
        onError: fail("Couldn't save the angle."),
        onSettled: settled,
      });
    }
  };

  /** Cancelling an edit review returns to the editor with the pending edit intact. */
  const cancelReview = () => {
    if (review?.kind === "edit") setEditing({ facet: review.facet, initial: review.fields });
    setReview(null);
  };

  const trawlAngle = (f: FacetSummary) => {
    const tagged = seeds.filter((s) => s.seed_facet_ids?.includes(f.id));
    if (tagged.length === 0) { toast.error(`No seeds for ${f.label} yet — use Find seeds… first.`); return; }
    onTrawl?.(tagged);
  };
  const atCap = maxFacets != null && facets.length >= maxFacets;
  // Spec 8.7: while the sheet is open, angle-changing actions wait (tabs still filter).
  const actionsHeld = busy || sheet != null;
  const heldReason = busy ? BUSY_REASON : SHEET_OPEN_REASON;
  const addButton = (
    <span className="ml-auto inline-flex items-center gap-1">
      <WhyDisabled reason={atCap ? `Your plan allows ${maxFacets} angles — upgrade for more` : actionsHeld ? heldReason : null}>
        <button
          type="button"
          disabled={atCap || actionsHeld}
          onClick={() => setSheet({ kind: "new" })}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-muted-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
        >
          + Add an angle
        </button>
      </WhyDisabled>
      {atCap && (
        <Link to="/settings/billing" className="text-xs font-medium text-primary underline underline-offset-2">
          Upgrade
        </Link>
      )}
    </span>
  );

  const reviewTitle = review?.kind === "retire" ? `Retire ${review.facet.label}?`
    : review?.kind === "restore" ? `Restore ${review.facet.label}?` : "Update the project question?";
  const reviewVerb = review?.kind === "retire" ? "Retire" : review?.kind === "restore" ? "Restore" : "Save";
  const lastAngle = review?.kind === "retire" && review.angles.length < 2 ? review.angles[0] : undefined;
  const retireEffects = review?.kind !== "retire" ? undefined : [
    "It leaves the angle tabs. Its scores are kept.",
    ...(lastAngle ? [
      `Only “${lastAngle.label}” is left, so the angle tabs go away.`,
      `“${lastAngle.label}” will be scored against your project question. Its own wording is replaced (Version history keeps it).`,
    ] : [
      "Papers whose best angle it was get a new best angle.",
      "Overall scores only change if you change the project question below.",
    ]),
  ];

  return (
    <>
      <FacetBar
        facets={facets} retired={retired} counts={counts} seedCounts={seedCounts} selected={selected}
        disabled={actionsHeld} disabledReason={heldReason} failed={failed}
        onSelect={onSelect} onEdit={(f) => setEditing({ facet: f })} onRetire={onRetire}
        onRestore={onRestore} onMove={onMove}
        onFindSeeds={(f) => setSheet({ kind: "seeds", facet: f })}
        onTrawlAngle={onTrawl && trawlAngle} trawling={trawling}
        trailing={addButton}
      />
      {sheet && (
        <AddAngleSheet
          key={sheet.kind === "seeds" ? `seeds:${sheet.facet.id}` : "new"}
          open mode={sheet} concept={concept} tagList
          onOpenChange={(o) => !o && setSheet(null)}
          onDone={(id) => { setSheet(null); onSelect(id); }}
        />
      )}
      {editing && (
        <EditAngleDialog
          open facet={editing.facet} initial={editing.initial} saving={busy}
          onSave={onSaveEdit} onCancel={() => setEditing(null)}
        />
      )}
      {review && (
        <ProjectQuestionReview
          open conceptId={conceptId} angles={review.angles} currentAbstract={abstract} currentKeywords={keywords}
          title={reviewTitle} confirmLabel={reviewVerb} submitting={busy}
          effects={retireEffects} redraft={!lastAngle}
          footnote={review.kind === "retire" ? <>Restore it any time from <b className="font-medium text-foreground">Retired angles</b> in the tab bar.</> : undefined}
          onSubmit={submitReview} onCancel={cancelReview}
        />
      )}
    </>
  );
}
